import AnimationComponent from '../../Component/Animation';
import Game from '../../Game';
import GameObject from '../../GameObject/GameObject';
import Sprite from '../../Prototype/Sprite';
import { ICardsState } from '../../State/Card/CardReducer';
import { GameActions } from '../../State/Game/GameActions';
import { ContextType } from '../../State/Game/IContext';
import { IGameState } from '../../State/Game/IGameState';
import { LevelStatus } from '../../State/Level/ILevelState';
import { LevelActions } from '../../State/Level/LevelActions';
import { getCompletedLevels } from '../../State/Player/PlayerLevelHelper';
import StateManager from '../../State/StateManager';
import CardsHelper from '../../Utility/CardsHelper';
import Point from '../../Utility/Point';
import SDKIntegrationsHelper from '../../Utility/SDK/SDKIntegrationsHelper';
import { FBTrackingEvent } from '../../Utility/Tracking/FBTrackingEvent';
import { AUDIO_LEVEL } from '../Audio';
import { AudioTrackType } from '../Audio/AudioClip';
import { StartAudio } from '../Audio/AudioHelper';
import LoadCardData, { LevelGenerationAlgorithm } from '../Card/LoadCardData';
import System from '../System';
import { ResourceDriver } from '../../Utility/Resource/ResourceManager';
import RenderSystem, { RenderSystemEvent } from '../Render/RenderSystem';
import PIXIRenderer from '../Render/PIXIRenderer';
import { scaleToFit } from '../../Utility/ScaletoFit';

export default class LevelSystem extends System {
    public backgroundSprite: Sprite;
    public levelData: any;
    public levelBackgroundKey: string;
    public levelNumber: number;
    public levelDataKey: string;
    public levelDataFile: string;
    public levelAudioClip: string;

    constructor(state: StateManager) {
        super(state);

        this.levelNumber = state.getState().levelState.levelNumber;
        this.levelDataKey = `level_${this.levelNumber}`;
        this.levelDataFile = state.getState().levelState.levelPath;
    }

    public async preload() {
        Game.resource.add(this.levelDataKey, this.levelDataFile);
        await Game.resource.wait(this.levelDataKey);

        this.levelData = Game.resource.get(this.levelDataKey);
        this.levelBackgroundKey = `resource/graphics/level_backgrounds/${this.levelData.settings.background}`;

        // From the level data load the background.
        // Game.resource.remove('level_background');

        if (!Game.resource.exists(this.levelBackgroundKey)) {
            Game.resource.add(this.levelBackgroundKey, this.levelBackgroundKey);
        }

        const worldNumber = Game.state.getState().worldState.worldNumber;

        this.levelAudioClip = AUDIO_LEVEL[worldNumber - 1];

        // Level UI.
        Game.resource.add('levelHUD', 'resource/data/ui/levelHUD.json');
        Game.resource.add(
            'levelCompleteMenu',
            'resource/data/ui/levelCompleteMenu.json'
        );
        Game.resource.add('noLivesMenu', 'resource/data/ui/noLivesMenu.json');
        Game.resource.add(
            'suitcaseBoosters',
            'resource/data/ui/suitcaseBoosters.json'
        );

        // Dragonbones UI VFX.
        Game.resource.add(
            'UI_VFX_skeleton_data',
            'resource/dragonbones/ui/skeleton.json'
        );
        Game.resource.add(
            'UI_VFX_texture',
            'resource/dragonbones/ui/texture.png'
        );
        Game.resource.add(
            'UI_VFX_texture_data',
            'resource/dragonbones/ui/texture.json'
        );

        // Level + common textures.
        Game.resource.add(
            'atlas_level',
            'resource/graphics/export/atlas_level.json'
        );
        Game.resource.add(
            'atlas_ui_common',
            'resource/graphics/export/atlas_ui_common.json'
        );
        Game.resource.add('atlas_hd', 'resource/graphics/export/atlas_hd.json');
        Game.resource.add(
            'atlas_ui_platform',
            'resource/graphics/export/atlas_ui_platform.json'
        );
        Game.resource.add(
            'resolveDataConflictMenu',
            'resource/data/ui/ResolveDataConflictMenu.json'
        );

        const backcardTexture = Game.state.getState().playerState.backcard;
        if (!Game.resource.existsAndLoaded(backcardTexture)) {
            Game.resource.add(
                backcardTexture,
                `resource/graphics/card_backs/${backcardTexture}.png`
            );
        }

        await Game.resource.load();
    }

    protected onStart(): void {
        this.backgroundSprite = GameObject.Instantiate<Sprite>(Sprite, {
            name: 'backgroundSprite',
            texture: this.levelBackgroundKey,
            anchor: new Point(0.5, 0.5),
            relativePosition: new Point(0.5, 0.5),
        });

        const width = this.backgroundSprite.container.width;
        const height = this.backgroundSprite.container.height;

        this.backgroundSprite.container.scale.set(
            scaleToFit(
                width,
                height,
                PIXIRenderer.renderer.width,
                PIXIRenderer.renderer.height
            )
        );
        RenderSystem.on(RenderSystemEvent.RESIZE, () => {
            this.backgroundSprite.container.scale.set(
                scaleToFit(
                    width,
                    height,
                    PIXIRenderer.renderer.width,
                    PIXIRenderer.renderer.height
                )
            );
        });
        this.backgroundSprite.addComponent(AnimationComponent);
        this.scene.addGameObject(this.backgroundSprite);
        this.loadBackgroundAssets();
    }

    private async loadBackgroundAssets() {
        // Load level audio.
        Game.resource.add(this.levelAudioClip, null, ResourceDriver.SECONDARY);
        Game.resource.wait(this.levelAudioClip).then(() => {
            StartAudio(this.scene, this.levelAudioClip, AudioTrackType.MUSIC, {
                loop: true,
            });
        });

        // Start preloading world atlases in background.
        Game.resource.add(
            'atlas_world',
            'resource/graphics/export/atlas_world.json'
        );
        Game.resource.add(
            'atlas_ui_world',
            'resource/graphics/export/atlas_ui_world.json'
        );

        Game.resource.add(
            'audioSpriteWinSequence',
            'resource/audio/export/win_sequence/win_sequence.json'
        );
        Game.resource.add(
            'audioSpriteLevel',
            'resource/audio/export/level/level.json'
        );

        await Game.resource.load();
    }

    public handleStateChanged(state: IGameState) {
        switch (state.levelState.status) {
            case LevelStatus.LOADING:
                const stars = [
                    this.levelData.settings.star_1,
                    this.levelData.settings.star_2,
                    this.levelData.settings.star_3,
                ];
                const extraCards = state.playerState.inventory.extraCards;

                const cardData = LoadCardData(
                    {
                        cardsInPlay: this.levelData.cards,
                        cardsInStack: [
                            ...this.levelData.settings.cards_in_stack,
                            ...Array(extraCards).fill(-1),
                        ],
                    },
                    new CardsHelper(),
                    state.levelState.levelNumber > 780
                        ? LevelGenerationAlgorithm.RANDOM
                        : LevelGenerationAlgorithm.SOLI1
                );

                // prettier-ignore
                const cardsState: ICardsState = {
                    cards: {
                        ...cardData.cardsInPlay,
                        ...cardData.cardsInStack,
                    },
                    inPlay: Object.values(cardData.cardsInPlay).map(c => c.id),
                    inStack: Object.values(cardData.cardsInStack).map(c => c.id),
                    folded: [],
                    discarded: [],
                    turns: 0,
                    tasks: [],
                    turnScore: 0,
                    undoHistory: [],
                    streakSequence: 0,
                    streakEnabled: false,
                    streakCards: 1,
                    streakScore: 0,
                };

                const levelIndex = getCompletedLevels(
                    state.playerState.levels[state.levelState.levelType]
                ).findIndex(
                    level => level.levelNumber === state.levelState.levelNumber
                );

                const winCriteria = this.levelData.settings.win_criteria;

                this.state.dispatch(
                    LevelActions.setLevelState({
                        cardsState: cardsState,
                        stars: stars,
                        coins: [0, 35, 55, 100],
                        firstAttempt: levelIndex === -1,
                        winCriteria: winCriteria,
                    })
                );
                break;
            case LevelStatus.LOADED:
                this.state.dispatch(LevelActions.levelIntro());

                SDKIntegrationsHelper.loadLevelLeaderboard(
                    state.levelState.levelNumber
                );

                // FTUX tracking.
                if (this.levelNumber > 0 && this.levelNumber <= 10) {
                    Game.sdk.gameTracking(
                        [
                            FBTrackingEvent.FTUE_03_GamefieldIsShow,
                            FBTrackingEvent.FTUE_11_Gamefield2IsShow,
                            FBTrackingEvent.FTUE_21_Gamefield3IsShow,
                            FBTrackingEvent.Level4_GameFieldShow,
                            FBTrackingEvent.Level5_GameFieldShow,
                            FBTrackingEvent.Level6_GameFieldShow,
                            FBTrackingEvent.Level7_GameFieldShow,
                            FBTrackingEvent.Level8_GameFieldShow,
                            FBTrackingEvent.Level9_GameFieldShow,
                            FBTrackingEvent.Level10_GameFieldShow,
                        ][this.levelNumber - 1]
                    );
                }

                break;
            case LevelStatus.INTRO:
                break;
            case LevelStatus.ACTIVE:
                break;
            case LevelStatus.EXITING:
                if (state.context.current === ContextType.DEFAULT) {
                    Game.state.dispatch(
                        GameActions.loadWorld(state.worldState.worldNumber)
                    );
                }
                break;
            case LevelStatus.RESTARTING:
                if (state.context.current === ContextType.DEFAULT) {
                    Game.state.dispatch(GameActions.restartLevel());
                }
                break;
        }
    }

    public init(): void {}

    protected onDestroy(): void {
        RenderSystem.off(RenderSystemEvent.RESIZE);
    }

    protected onLateUpdate(): void {}

    protected onStop(): void {}

    protected onUpdate(): void {}
}
